import React, { useEffect, useState } from "react";
import { Plus, Search, Pencil, Trash2, Building2, Phone, Loader2, History, Mail, Landmark, Wallet, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, formatApiError, openPdf } from "@/lib/api";
import { Card } from "@/components/Cards";
import { matches } from "@/components/SearchBox";
import { takeJump } from "@/lib/jump";
import PriceHistoryDialog from "@/components/PriceHistoryDialog";
import PriceSimulator from "@/components/PriceSimulator";
import PaymentDialog from "@/components/PaymentDialog";
import InvoiceDialog from "@/components/InvoiceDialog";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const EMPTY = { name: "", contact: "", phone: "", email: "", tax_number: "", tax_office: "", short_name: "", unit_price: "", breakfast_price: "", dinner_price: "", payment_term_days: 30, address: "", vat_rate: 0, no_label: false, label_copies: 1 };

export default function Customers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState(() => takeJump("customers")?.name || "");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);
  const [histCust, setHistCust] = useState(null);
  const [payCust, setPayCust] = useState(null);
  const [invCust, setInvCust] = useState(null);
  const [stmtBusy, setStmtBusy] = useState(null);
  const [balSort, setBalSort] = useState("desc");

  const openStatement = async (c) => {
    setStmtBusy(c.id);
    try {
      const res = await api.get(`/ledger/export/${c.id}`, { params: { format: "pdf" }, responseType: "blob" });
      openPdf(res.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Ekstre oluşturulamadı"); }
    finally { setStmtBusy(null); }
  };

  const load = async () => {
    const { data } = await api.get("/customers");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({ name: c.name, contact: c.contact, phone: c.phone, email: c.email || "", tax_number: c.tax_number || "",
      tax_office: c.tax_office || "", short_name: c.short_name || "", unit_price: c.unit_price, breakfast_price: c.breakfast_price || "", dinner_price: c.dinner_price || "", vat_rate: c.vat_rate || 0,
      payment_term_days: c.payment_term_days, address: c.address, no_label: !!c.no_label, label_copies: c.label_copies || 1 });
    setEditId(c.id); setOpen(true);
  };

  const setVat = async (c, rate) => {
    if ((c.vat_rate || 0) === rate) return;
    try {
      await api.put("/customers/vat-bulk", { customer_ids: [c.id], vat_rate: rate });
      toast.success(rate ? `${c.name}: satış fiyatına %${rate} KDV eklendi` : `${c.name}: KDV kaldırıldı`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Şirket ünvanı gerekli"); return; }
    setSaving(true);
    try {
      const payload = { ...form, unit_price: parseFloat(form.unit_price) || 0, breakfast_price: parseFloat(form.breakfast_price) || 0, dinner_price: parseFloat(form.dinner_price) || 0, vat_rate: Number(form.vat_rate) || 0,
        payment_term_days: parseInt(form.payment_term_days, 10) || 0, label_copies: Math.max(1, parseInt(form.label_copies, 10) || 1) };
      if (editId) { await api.put(`/customers/${editId}`, payload); toast.success("Müşteri güncellendi"); }
      else { await api.post("/customers", payload); toast.success("Müşteri eklendi"); }
      setOpen(false); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/customers/${delId}`);
      toast.success("Müşteri silindi");
      setDelId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = items.filter((c) => matches(search, c.name, c.short_name, c.contact, c.phone));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Müşteriler</h1>
          <p className="text-sm text-muted-foreground mt-1">Firma bilgileri, kişi başı anlaşmalı fiyat ve cari durum.</p>
        </div>
        <Button data-testid="customer-add-button" onClick={openNew} className="rounded-full h-11 px-6 font-semibold gap-2">
          <Plus className="h-4 w-4" /> Yeni Müşteri
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Müşteri ara…" data-testid="customer-search-input" value={search}
          onChange={(e) => setSearch(e.target.value)} className="h-11 pl-9" />
      </div>

      <Tabs defaultValue="cards" className="space-y-4">
        <TabsList data-testid="customer-tabs">
          <TabsTrigger value="cards" data-testid="customer-tab-cards">Kartlar</TabsTrigger>
          <TabsTrigger value="balances" data-testid="customer-tab-balances">Bakiyeler</TabsTrigger>
        </TabsList>

        <TabsContent value="cards">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <Card key={c.id} data-testid={`customer-card-${c.id}`} className="p-5 hover:border-border/80 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-heading font-semibold truncate">{c.short_name || c.name}</div>
                  {c.short_name && <div className="text-[11px] text-muted-foreground truncate" data-testid={`customer-fullname-${c.id}`}>{c.name}</div>}
                  {c.no_label && <div className="text-[10px] text-amber-400 font-semibold" data-testid={`customer-no-label-${c.id}`}>Etiket basılmaz</div>}
                  {!c.no_label && c.label_copies > 1 && <div className="text-[10px] text-muted-foreground" data-testid={`customer-label-copies-${c.id}`}>{c.label_copies} etiket/gün</div>}
                  <div className="text-xs text-muted-foreground truncate">{c.contact || "—"}</div>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => openStatement(c)} data-testid={`customer-statement-${c.id}`} title="Cari Ekstre PDF" disabled={stmtBusy === c.id}
                  className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-primary disabled:opacity-50">
                  {stmtBusy === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                </button>
                <button onClick={() => setHistCust(c)} data-testid={`customer-price-history-${c.id}`} title="Fiyat Geçmişi"
                  className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-primary">
                  <History className="h-4 w-4" />
                </button>
                <button onClick={() => openEdit(c)} data-testid={`customer-edit-${c.id}`}
                  className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-foreground">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => setDelId(c.id)} data-testid={`customer-delete-${c.id}`}
                  className="h-8 w-8 rounded-lg hover:bg-destructive/10 flex items-center justify-center text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
              <Phone className="h-3.5 w-3.5" /> {c.phone || "—"}
              <span className="mx-1">•</span> Vade: {c.payment_term_days} gün
            </div>
            <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground flex-wrap" data-testid={`customer-meta-${c.id}`}>
              <Mail className="h-3.5 w-3.5" /> <span className="truncate max-w-[180px]">{c.email || "—"}</span>
              <span className="mx-1">•</span> <Landmark className="h-3.5 w-3.5" /> {c.tax_office || "—"} / {c.tax_number || "—"}
            </div>

            {user?.role === "admin" && <div className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2" data-testid={`customer-vat-row-${c.id}`}>
              <span className="text-xs text-muted-foreground">Satış fiyatına KDV</span>
              <div className="inline-flex rounded-lg bg-muted/60 p-0.5">
                {[0, 5, 10].map((r) => (
                  <button key={r} onClick={() => setVat(c, r)} data-testid={`customer-vat-${r}-${c.id}`}
                    className={`h-7 px-3 rounded-md text-xs font-semibold transition-colors ${(c.vat_rate || 0) === r ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}>%{r} KDV</button>
                ))}
              </div>
            </div>}
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              <div className="rounded-lg bg-muted/50 py-2">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Öğle{c.vat_rate > 0 && " · KDV dahil"}</div>
                <div className="font-mono font-semibold text-sm mt-0.5">{TRY(c.unit_price * (1 + (c.vat_rate || 0) / 100))}</div>
                {c.vat_rate > 0 && <div className="text-[10px] text-muted-foreground" data-testid={`customer-vat-badge-${c.id}`}>Net {TRY(c.unit_price)} + %{c.vat_rate}</div>}
                {c.breakfast_price > 0 && <div className="text-[10px] text-amber-400 font-semibold" data-testid={`customer-breakfast-badge-${c.id}`}>Kahvaltı {TRY(c.breakfast_price * (1 + (c.vat_rate || 0) / 100))}</div>}
                {c.dinner_price > 0 && <div className="text-[10px] text-sky-400 font-semibold" data-testid={`customer-dinner-badge-${c.id}`}>Akşam {TRY(c.dinner_price * (1 + (c.vat_rate || 0) / 100))}</div>}
              </div>
              <div className="rounded-lg bg-muted/50 py-2" data-testid={`customer-debit-${c.id}`}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Borç</div>
                <div className="font-mono font-semibold text-sm mt-0.5 text-rose-400">{TRY(c.debit)}</div>
              </div>
              <div className="rounded-lg bg-muted/50 py-2" data-testid={`customer-credit-${c.id}`}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Tahsilat</div>
                <div className="font-mono font-semibold text-sm mt-0.5 text-emerald-400">{TRY(c.credit)}</div>
              </div>
              <div className="rounded-lg bg-muted/50 py-2" data-testid={`customer-balance-${c.id}`}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Bakiye</div>
                <div className={`font-mono font-semibold text-sm mt-0.5 ${c.balance > 0 ? "text-amber-400" : "text-foreground"}`}>{TRY(c.balance)}</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => setPayCust(c)} data-testid={`customer-payment-btn-${c.id}`}
                className="h-9 gap-2 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300">
                <Wallet className="h-4 w-4" /> Tahsilat Ekle
              </Button>
              <Button variant="outline" onClick={() => setInvCust(c.id)} data-testid={`customer-invoice-btn-${c.id}`}
                className="h-9 gap-2 border-rose-500/40 text-rose-400 hover:bg-rose-500/10 hover:text-rose-300">
                <FileText className="h-4 w-4" /> Fatura Ekle
              </Button>
            </div>
          </Card>
        ))}
          </div>
        </TabsContent>

        <TabsContent value="balances" className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">Sırala:</span>
            <div className="inline-flex rounded-lg bg-muted/60 p-0.5">
              {[["desc", "Borç ↓"], ["asc", "Borç ↑"], ["name", "İsim A→Z"]].map(([val, lbl]) => (
                <button key={val} onClick={() => setBalSort(val)} data-testid={`customer-balances-sort-${val}`}
                  className={`h-8 px-3 rounded-md text-xs font-semibold transition-colors ${balSort === val ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}>{lbl}</button>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-border overflow-hidden" data-testid="customer-balances-table">
            {filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Müşteri bulunamadı.</div>
            ) : (
              <>
                {[...filtered].sort((a, b) =>
                  balSort === "name"
                    ? (a.short_name || a.name || "").localeCompare(b.short_name || b.name || "", "tr")
                    : balSort === "asc"
                      ? (a.balance || 0) - (b.balance || 0)
                      : (b.balance || 0) - (a.balance || 0)
                ).map((c) => (
                  <div key={c.id} data-testid={`customer-balances-row-${c.id}`}
                    className="flex items-center justify-between gap-4 px-4 py-3 border-b border-border last:border-b-0 hover:bg-accent/40 transition-colors">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{c.short_name || c.name}</div>
                      {c.short_name && <div className="text-[11px] text-muted-foreground truncate">{c.name}</div>}
                    </div>
                    <div className={`font-mono font-semibold text-sm shrink-0 ${c.balance > 0 ? "text-amber-400" : "text-foreground"}`}
                      data-testid={`customer-balances-amount-${c.id}`}>{TRY(c.balance)}</div>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-4 px-4 py-3 bg-muted/60 font-semibold" data-testid="customer-balances-total">
                  <span>Genel Toplam ({filtered.length} müşteri)</span>
                  <span className="font-mono text-sm text-amber-400" data-testid="customer-balances-total-amount">
                    {TRY(filtered.reduce((s, c) => s + (c.balance || 0), 0))}
                  </span>
                </div>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Müşteri Düzenle" : "Yeni Müşteri"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <Field label="Şirket Ünvanı *" className="sm:col-span-2">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="customer-form-name" placeholder="Örn: Akasya Sanayi A.Ş." />
            </Field>
            <Field label="Kısa Ad" className="sm:col-span-2">
              <Input value={form.short_name} onChange={(e) => setForm({ ...form, short_name: e.target.value })}
                data-testid="customer-form-short-name" placeholder="Örn: Akasya — listelerde ve PDF'lerde bu ad kullanılır" />
            </Field>
            <Field label="Yetkili">
              <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })}
                data-testid="customer-form-contact" />
            </Field>
            <Field label="Telefon">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="customer-form-phone" placeholder="05xx xxx xx xx" />
            </Field>
            <Field label="E-posta" className="sm:col-span-2">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="customer-form-email" placeholder="muhasebe@firma.com" />
            </Field>
            <Field label="Vergi Dairesi">
              <Input value={form.tax_office} onChange={(e) => setForm({ ...form, tax_office: e.target.value })}
                data-testid="customer-form-tax-office" placeholder="Örn: Maslak V.D." />
            </Field>
            <Field label="Vergi Numarası">
              <Input value={form.tax_number} onChange={(e) => setForm({ ...form, tax_number: e.target.value })}
                data-testid="customer-form-tax-number" className="font-mono" placeholder="10 haneli" />
            </Field>
            <Field label="Öğle Yemeği Kişi Başı Fiyat (₺)">
              <Input type="number" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                data-testid="customer-form-price" placeholder="0" className="font-mono" />
              <div className="mt-2 flex items-center gap-3" data-testid="customer-form-vat-label">
                <span className="text-xs text-muted-foreground">Satış fiyatına KDV</span>
                <div className="inline-flex rounded-lg bg-muted/60 p-0.5">
                  {[0, 5, 10].map((r) => (
                    <button type="button" key={r} onClick={() => setForm({ ...form, vat_rate: r })} data-testid={`customer-form-vat-${r}`}
                      className={`h-8 px-3 rounded-md text-xs font-semibold transition-colors ${Number(form.vat_rate) === r ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}>%{r} KDV</button>
                  ))}
                </div>
                <input type="checkbox" className="hidden" checked={Number(form.vat_rate) > 0} readOnly data-testid="customer-form-vat" />
                {Number(form.vat_rate) > 0 && form.unit_price && <span className="text-xs text-muted-foreground font-mono">→ KDV dahil {TRY(parseFloat(form.unit_price) * (1 + Number(form.vat_rate) / 100))}</span>}
              </div>
            </Field>
            <Field label="Kahvaltı Kişi Başı Fiyat (₺)">
              <Input type="number" value={form.breakfast_price} onChange={(e) => setForm({ ...form, breakfast_price: e.target.value })}
                data-testid="customer-form-breakfast-price" placeholder="Boşsa kahvaltı girişinde listelenmez" className="font-mono" />
              {Number(form.vat_rate) > 0 && form.breakfast_price && <div className="mt-1 text-xs text-muted-foreground font-mono">→ KDV dahil {TRY(parseFloat(form.breakfast_price) * (1 + Number(form.vat_rate) / 100))}</div>}
            </Field>
            <Field label="Akşam Yemeği Kişi Başı Fiyat (₺)">
              <Input type="number" value={form.dinner_price} onChange={(e) => setForm({ ...form, dinner_price: e.target.value })}
                data-testid="customer-form-dinner-price" placeholder="Boşsa akşam girişinde listelenmez" className="font-mono" />
              {Number(form.vat_rate) > 0 && form.dinner_price && <div className="mt-1 text-xs text-muted-foreground font-mono">→ KDV dahil {TRY(parseFloat(form.dinner_price) * (1 + Number(form.vat_rate) / 100))}</div>}
              <div className="mt-1 text-[11px] text-muted-foreground">Gece vardiyası için de bu fiyat kullanılır.</div>
            </Field>
            <Field label="Ödeme Vadesi (gün)">
              <Input type="number" value={form.payment_term_days}
                onChange={(e) => setForm({ ...form, payment_term_days: e.target.value })}
                data-testid="customer-form-term" className="font-mono" />
            </Field>
            <Field label="Adres" className="sm:col-span-2">
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="customer-form-address" />
            </Field>
            <label className="sm:col-span-2 flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="customer-form-no-label">
              <input type="checkbox" checked={!!form.no_label} onChange={(e) => setForm({ ...form, no_label: e.target.checked })} className="h-4 w-4 accent-primary" />
              Etiket PDF'sine ekleme (bu firma için etiket basılmaz)
            </label>
            {!form.no_label && (
              <Field label="Etiket Adedi (günlük)">
                <Input type="number" min={1} max={20} value={form.label_copies}
                  onChange={(e) => setForm({ ...form, label_copies: e.target.value })}
                  data-testid="customer-form-label-copies" className="font-mono" />
              </Field>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} disabled={saving} data-testid="customer-form-save" className="gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {user?.role === "admin" && <PriceSimulator customers={items} />}

      <PriceHistoryDialog customer={histCust} onClose={() => setHistCust(null)} />
      <PaymentDialog customer={payCust} onClose={() => setPayCust(null)} onSaved={load} />
      <InvoiceDialog open={!!invCust} onClose={() => setInvCust(null)} kind="sale" parties={items} defaultPartyId={invCust || ""} onSaved={load} />

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Müşteri silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu müşteriye ait tüm cari hareketler ve günlük kayıtlar da silinecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="customer-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);
